import storageApi from '../Api/storageApi';
import aiApi from '../Api/aiApi';
import drawUtils from '../pages/LessonPage/drawUtils';

// Resolves an AI "image" component into pixels. Search comes first: for the things a lesson
// actually needs a picture of - a map, a region, a historical figure, a monument, an artefact -
// a real photograph or a real map is correct, where a generated one is invented and only looks
// plausible. Generation is the fallback so a component is never left blank.
//
// Search results are unvetted, so a candidate has to earn its way onto the board. It is judged
// twice: once on the metadata the search API returns for free, and again on the decoded pixels.

// One search call returns up to 10 results for the same quota cost, so ask for a healthy
// shortlist rather than trusting the top hit.
const SEARCH_CANDIDATES = 8;

// The board canvas is 1440x720 and an image element is 300-500 board px wide, so anything under
// this is already soft before the teacher zooms in.
const MIN_LONG_EDGE = 600;
const MIN_SHORT_EDGE = 300;

// Banners, sprite sheets and sidebar strips are never the picture a lesson wanted.
const MIN_ASPECT = 0.4;
const MAX_ASPECT = 3;

// A file this small at an acceptable resolution is a placeholder, a spacer or a flat colour.
const MIN_BYTE_SIZE = 10 * 1024;
const MAX_BYTE_SIZE = 8 * 1024 * 1024;

// Formats that do not decode into a usable still picture: GIF gives us one frame of an
// animation, SVG can rasterise to anything or nothing, the rest are icons or poorly supported.
// This is a blocklist rather than an allowlist because the search API often reports a truncated
// "image/" with no subtype, and rejecting those threw away perfectly good pictures.
const BLOCKED_MIME_SUBTYPES = ['gif', 'svg+xml', 'x-icon', 'vnd.microsoft.icon', 'bmp', 'tiff'];

// Stock libraries serve watermarked previews, which look like a picture in the search result
// and like a defaced picture on the board.
const WATERMARKED_HOSTS = [
    'shutterstock',
    'istockphoto',
    'dreamstime',
    'alamy',
    '123rf',
    'depositphotos',
    'gettyimages',
    'vectorstock',
    'canstockphoto',
    'bigstockphoto',
    'stock.adobe',
];

// Last line of defence against "image unavailable" placeholders and solid colour fills, which
// pass every metadata check. Luminance spread over a 32x32 downsample, on a 0-255 scale.
const DETAIL_SAMPLE = 32;
const MIN_DETAIL_STDDEV = 6;

// Both AIResponseRenderer instances mounted for one response resolve the same components, and
// "Add to board" can run while the preview is still resolving. Each attempt costs a search
// quota unit or a paid generation, so concurrent callers share one in-flight request. The
// entry is dropped once it settles, so resolving the same component later really re-runs.
const inFlight = new Map();

// Search results are cross-origin. They come through the existing image proxy, which is what
// keeps the canvas untainted so getImageData and toDataURL work.
const proxied = (url) => {
    let target = url;
    try {
        target = decodeURIComponent(url);
    } catch (e) {
        // Already-decoded URLs with a stray % throw here; the raw value is fine to use.
    }
    return `/api/private/proxy-images?url=${encodeURIComponent(target)}`;
};

// Paints onto white first: a PNG with a transparent background would otherwise turn black when
// encoded as JPEG, which is exactly what line-art diagrams tend to be.
const drawOnWhite = (image, canvas, width, height) => {
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, 0, 0, width, height);
    return ctx;
};

// ---- stage 1: judge the metadata, before spending a download on it -------------------------

const metadataVerdict = (item) => {
    if (!item || !item.link) return { ok: false, why: 'no link' };

    const subtype = String(item.mime || item.fileFormat || '').toLowerCase().split('/')[1] || '';
    if (subtype && BLOCKED_MIME_SUBTYPES.includes(subtype)) return { ok: false, why: `mime ${subtype}` };

    const host = String(item.displayLink || item.link).toLowerCase();
    const watermarked = WATERMARKED_HOSTS.find((h) => host.includes(h));
    if (watermarked) return { ok: false, why: `watermarked (${watermarked})` };

    const meta = item.image || {};
    const width = Number(meta.width);
    const height = Number(meta.height);
    // Missing dimensions are not a reason to reject: the pixel check downstream still applies.
    if (width > 0 && height > 0) {
        const longEdge = Math.max(width, height);
        const shortEdge = Math.min(width, height);
        if (longEdge < MIN_LONG_EDGE) return { ok: false, why: `${width}x${height} too small` };
        if (shortEdge < MIN_SHORT_EDGE) return { ok: false, why: `${width}x${height} too thin` };

        const aspect = width / height;
        if (aspect < MIN_ASPECT || aspect > MAX_ASPECT) {
            return { ok: false, why: `aspect ${aspect.toFixed(2)}` };
        }
    }

    const bytes = Number(meta.byteSize);
    if (bytes > 0 && bytes < MIN_BYTE_SIZE) return { ok: false, why: `${bytes} bytes` };
    if (bytes > MAX_BYTE_SIZE) return { ok: false, why: `${bytes} bytes too large` };

    return { ok: true };
};

// ---- stage 2: judge the decoded pixels ------------------------------------------------------

const hasEnoughDetail = (image) => {
    const ctx = drawOnWhite(image, document.createElement('canvas'), DETAIL_SAMPLE, DETAIL_SAMPLE);

    let data;
    try {
        data = ctx.getImageData(0, 0, DETAIL_SAMPLE, DETAIL_SAMPLE).data;
    } catch (e) {
        // Only a tainted canvas throws here, which the proxy should prevent. Treat it as
        // "cannot judge" rather than rejecting a picture that may be perfectly good.
        return true;
    }

    const count = DETAIL_SAMPLE * DETAIL_SAMPLE;
    let sum = 0;
    let sumSquares = 0;
    for (let i = 0; i < data.length; i += 4) {
        const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        sum += luma;
        sumSquares += luma * luma;
    }
    const mean = sum / count;
    const stddev = Math.sqrt(Math.max(0, sumSquares / count - mean * mean));
    return stddev >= MIN_DETAIL_STDDEV;
};

const pixelVerdict = (image) => {
    if (!image) return { ok: false, why: 'did not load' };

    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (!(width > 0) || !(height > 0)) return { ok: false, why: 'zero size' };

    // The metadata may have lied, or the proxy may have served an error placeholder.
    if (Math.max(width, height) < MIN_LONG_EDGE || Math.min(width, height) < MIN_SHORT_EDGE) {
        return { ok: false, why: `decoded ${width}x${height} too small` };
    }
    const aspect = width / height;
    if (aspect < MIN_ASPECT || aspect > MAX_ASPECT) {
        return { ok: false, why: `decoded aspect ${aspect.toFixed(2)}` };
    }
    if (!hasEnoughDetail(image)) return { ok: false, why: 'near-blank image' };

    return { ok: true };
};

// ---- search ---------------------------------------------------------------------------------

const searchForImage = async (query) => {
    const res = await storageApi.searchGoogle(encodeURIComponent(query), SEARCH_CANDIDATES, 0);
    const items = Array.isArray(res && res.data) ? res.data : [];

    // Google's result order carries the relevance signal, and it is by far the most valuable
    // thing we have: re-sorting these by resolution surfaces high-megapixel junk - jigsaw
    // puzzle photos, craft reproductions, tour adverts - over the schoolroom map that ranked
    // first. So quality is only ever a filter here, never a re-ranking. Take the first
    // candidate that is relevant enough to rank AND good enough to look at.
    const rejected = [];
    for (const item of items) {
        const meta = metadataVerdict(item);
        if (!meta.ok) {
            rejected.push(meta.why);
            continue;
        }

        const image = await drawUtils.buildImageFromGoogleProxy(proxied(item.link));
        const pixels = pixelVerdict(image);
        if (pixels.ok) {
            return image;
        }
        rejected.push(pixels.why);
    }

    if (rejected.length) {
        console.warn(`Rejected ${rejected.length} image results for "${query}":`, rejected.join(', '));
    }
    return null;
};

// ---- resolution -----------------------------------------------------------------------------

// Runs the data URL through the same downscaling a pasted image gets, and reports the size the
// board should use: StickyImage stretches the <img> to its element box, so the box has to match
// the picture's real aspect ratio.
const normalize = async (dataUrl) => {
    const resized = await drawUtils.imageResize(dataUrl);
    return { dataUrl: resized.data, naturalWidth: resized.w, naturalHeight: resized.h };
};

const toDataUrl = (image) => {
    const canvas = document.createElement('canvas');
    drawOnWhite(image, canvas, image.naturalWidth, image.naturalHeight);
    // JPEG keeps the upload small; the white fill above means nothing needed transparency.
    return canvas.toDataURL('image/jpeg', 0.92);
};

const resolve = async (component, subject) => {
    try {
        const image = await searchForImage(component.content);
        if (image) {
            return { ...(await normalize(toDataUrl(image))), source: 'search' };
        }
        console.warn('No image result passed the quality checks, generating one instead:', component.content);
    } catch (error) {
        console.error('Image search failed, generating one instead:', error);
    }

    // imagePrompt is an English description written for the image model; content is a search
    // query in the lesson language, which makes a poor generation prompt but is better than
    // nothing if the model omitted the fallback.
    const res = await aiApi.generateIllustration(component.imagePrompt || component.content, {
        width: component.width,
        height: component.height,
        subject,
    });
    if (!res || !res.dataUrl) {
        return null;
    }
    return { ...(await normalize(res.dataUrl)), source: 'generated' };
};

export const resolveIllustration = (component, options = {}) => {
    const query = component && typeof component.content === 'string' ? component.content.trim() : '';
    if (!query) {
        return Promise.resolve(null);
    }

    const subject = options.subject || '';
    // The subject only steers the generation fallback, but it still changes the result, so it
    // belongs in the identity.
    const key = `${subject}|${query}`;
    const existing = inFlight.get(key);
    if (existing) {
        return existing;
    }

    const request = resolve(component, subject).finally(() => inFlight.delete(key));
    inFlight.set(key, request);
    return request;
};

export default { resolveIllustration };
